import type { TaskOwnerResolution } from "@app/types/project_task";
import type { ModelId } from "@app/types/shared/model_id";

export const unclearOwner = "unclear" as const;

export type ProposedOwnerInput =
  | { name?: string | null; email?: string | null }
  | typeof unclearOwner;

export type OwnerCandidate = {
  id: ModelId;
  sId: string;
  email: string;
  fullName: string;
};

export type ResolvedProposedOwner = {
  name: string | null;
  email: string | null;
  user: OwnerCandidate | null;
  resolution: TaskOwnerResolution;
  ambiguity: string | null;
};

/**
 * The people a proposed owner can resolve to. `container` holds whoever a task
 * in this place can be assigned to: a Pod's members, or the run user alone for
 * a personal list. `workspace` holds every active member of this workspace.
 */
export type OwnerDirectory = {
  container: OwnerCandidate[];
  workspace: OwnerCandidate[];
  // Addresses that are one of the container's people under another spelling,
  // such as the calendar's `self` attendee for the run user.
  aliases: Map<string, OwnerCandidate>;
};

function fold(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

export function cleanedOwnerText(
  value: string | null | undefined
): string | null {
  const trimmed = value?.replace(/\s+/g, " ").trim();
  return trimmed ? trimmed : null;
}

function byEmail(
  people: OwnerCandidate[],
  email: string
): OwnerCandidate | null {
  return people.find((person) => fold(person.email) === email) ?? null;
}

// Words a producer writes in place of an owner it could not name.
const placeholderNames = new Set([
  "unclear",
  "unknown",
  "tbd",
  "tba",
  "n/a",
  "none",
  "nobody",
  "?",
  "-",
]);

function byFirstName(
  people: OwnerCandidate[],
  firstName: string
): OwnerCandidate[] {
  return people.filter(
    (person) => fold(person.fullName).split(" ")[0] === firstName
  );
}

function byName(people: OwnerCandidate[], name: string): OwnerCandidate[] {
  return people.filter((person) => fold(person.fullName) === name);
}

/**
 * A proposed owner, resolved to a person this workspace knows or kept by name.
 * It never falls back to a default: a named person nobody here matches is an
 * outside contact (`external`), and only a missing or ambiguous owner is
 * `unclear`, never whoever ran the agent.
 *
 * Another workspace is out of reach on purpose. Saying that an address has a
 * MemoAir account elsewhere is a disclosure about another tenant, and nothing
 * can deliver work across workspaces, so such a person reads as `external`.
 */
export function resolveProposedOwner(
  input: ProposedOwnerInput | null | undefined,
  directory: OwnerDirectory
): ResolvedProposedOwner {
  const name =
    input && input !== unclearOwner ? cleanedOwnerText(input.name) : null;
  const email =
    input && input !== unclearOwner
      ? cleanedOwnerText(input.email)?.toLowerCase()
      : null;
  const kept = { name, email: email ?? null };

  if (!name && !email) {
    return { ...kept, user: null, resolution: "unclear", ambiguity: null };
  }

  if (email) {
    const inContainer =
      byEmail(directory.container, email) ?? directory.aliases.get(email);
    if (inContainer) {
      return {
        ...kept,
        user: inContainer,
        resolution: "member",
        ambiguity: null,
      };
    }
    const inWorkspace = byEmail(directory.workspace, email);
    if (inWorkspace) {
      return {
        ...kept,
        user: inWorkspace,
        resolution: "workspace_member",
        ambiguity: null,
      };
    }
    if (!name) {
      return { ...kept, user: null, resolution: "external", ambiguity: null };
    }
  }

  const folded = fold(name ?? "");
  if (placeholderNames.has(folded)) {
    return { ...kept, user: null, resolution: "unclear", ambiguity: null };
  }

  for (const [people, resolution] of [
    [directory.container, "member"],
    [directory.workspace, "workspace_member"],
  ] as const) {
    const matches = byName(people, folded);
    if (matches.length === 1) {
      return {
        ...kept,
        user: matches[0],
        resolution,
        ambiguity: email
          ? `Matched "${name}" by name; nobody here has ${email}.`
          : null,
      };
    }
    if (matches.length > 1) {
      return {
        ...kept,
        user: null,
        resolution: "unclear",
        ambiguity: `"${name}" matches ${matches.map((m) => m.fullName).join(" and ")}.`,
      };
    }
  }

  // A lone first name, as people say it in a meeting ("Varun, can you…"), may
  // be a colleague or someone from the other side of the room. It is noted
  // for the reviewer and left unclear, so it never assigns anyone by itself.
  if (!folded.includes(" ")) {
    const candidates = [
      ...byFirstName(directory.container, folded),
      ...byFirstName(directory.workspace, folded),
    ].filter(
      (person, index, all) => all.findIndex((p) => p.id === person.id) === index
    );
    if (candidates.length > 0) {
      return {
        ...kept,
        user: null,
        resolution: "unclear",
        ambiguity: `"${name}" may be ${candidates.map((m) => m.fullName).join(" or ")}.`,
      };
    }
  }

  // A name that matches nobody here is an outside contact, kept by name: only
  // no name at all, or one that matches several people, is unclear.
  return { ...kept, user: null, resolution: "external", ambiguity: null };
}

/**
 * Who a Pod task an agent writes is assigned to when a person asked for it: the
 * member the asker named outright, else the proposed owner when they are in
 * the Pod, else nobody. The writer and the approval card both read this, so
 * the card cannot promise an assignee the task will not get.
 */
export function podAssigneeFor(
  {
    assigneeUserSId,
    proposedOwner,
  }: {
    assigneeUserSId?: string | null;
    proposedOwner: ResolvedProposedOwner | null;
  },
  container: OwnerCandidate[]
): { assignee: OwnerCandidate | null; unknownAssignee: boolean } {
  if (assigneeUserSId) {
    const assignee =
      container.find((member) => member.sId === assigneeUserSId) ?? null;
    return { assignee, unknownAssignee: assignee === null };
  }

  return {
    assignee:
      proposedOwner?.resolution === "member" ? proposedOwner.user : null,
    unknownAssignee: false,
  };
}
